
import { defineComponent, reactive, ref } from 'vue';
import { useList } from '@/hooks/use-list';
import {
  importConfig,
  getConfigList,
  updateConfigEnable,
  getEnvList,
  syncConfigByEnv,
  configRedeclare,
} from '@/api/message-center';
import { exportByBlob } from '@/core/utils/file-download';
import type { IConfigPageListItem, IConfigPageReq, IConfigSyncConfigByEnvReq } from '@/api/message-center/type.d';
import { CONFIG_IS_ENABLED_LIST, CONFIG_IS_ENABLED_ENUM } from '@/constant/message-center';
import { ElForm, ElMessage } from 'element-plus';
import EditDialog from './components/edit-dialog';
import usePermissionConfig from './use-permission-config';

export default defineComponent({
  components: {
    EditDialog,
  },
  setup() {
    const {
      params,
      tableTotal,
      tableData,
      tableLoading,
      handleSearch,
      handleReset,
      handleSizeChange,
      handleCurrentChange,
    } = useList<IConfigPageListItem, IConfigPageReq>({
      request: {
        api: getConfigList,
        params: {
          configCode: '',
          configName: '',
          exchange: '',
          queue: '',
          routing: '',
          executeService: '',
          sender: '',
          isEnable: CONFIG_IS_ENABLED_ENUM.ENABLED,
          pageNum: 1,
          pageSize: 10,
        },
        handleParams(paramsObj) {
          return paramsObj;
        },
      },
    });

    const editFormConfig = ref({
      visible: false,
      configId: '',
    });

    const handleEditConfigInfo = (row = {} as IConfigPageListItem) => {
      editFormConfig.value.visible = true;
      editFormConfig.value.configId = row.configId;
    };
    const handleEidtSuccess = () => {
      handleSearch(params.value.pageNum as number);
    };

    const handleUpdateConfigEnable = async (row: IConfigPageListItem) => {
      await updateConfigEnable(
        row.isEnable === CONFIG_IS_ENABLED_ENUM.ENABLED
          ? CONFIG_IS_ENABLED_ENUM.DISABLED
          : CONFIG_IS_ENABLED_ENUM.ENABLED,
        row.configId,
      );
      ElMessage.success('操作成功');
      handleSearch(params.value.pageNum as number);
    };

    const handleExportExcel = async () => {
      const date = new Date();
      await exportByBlob({
        method: 'post',
        url: '/mq/ops/v1/config/export',
        data: {
          ...params.value,
          pageSize: tableTotal.value,
        },
        filename: `消息配置${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}.xlsx`,
        loading: true,
      });
      ElMessage.success('导出成功');
    };

    const handleImportExcel = async (e: Event) => {
      try {
        const el: any = e.target;
        const { files }: { files: FileList; } = el;
        if (files.length === 0) return;
        const formData = new FormData();
        const file = files[0];
        if (!['xlsx', 'xls'].includes(file.name.split('.').pop()!)) {
          ElMessage.error('必须上传excel文件');
          return;
        }
        formData.append('file', file);
        el.value = null;
        await importConfig(formData);
        ElMessage.success('导入成功');
        handleSearch();
      } catch (error) {
        console.log(error, '上传excel失败');
      }
    };

    const envList = ref<string[]>([]);
    const init = async () => {
      handleSearch();
      const { data } = await getEnvList();
      envList.value = data;
    };

    init();
    const permissionConfig = usePermissionConfig();

    // 批量同步
    const formDOM = ref<InstanceType<typeof ElForm> | null>(null);
    const bacthEnvData = reactive<IConfigSyncConfigByEnvReq>({
      configCodes: [],
      targetEnv: '',
    });
    const dialogVisible = ref(false);
    const selectedRow = ref<IConfigPageListItem[]>([]);
    const handleSelectionChange = (lists: IConfigPageListItem[]) => {
      selectedRow.value = lists;
    };
    const showBatchEnv = () => {
      dialogVisible.value = true;
    };
    const closeBatchEnv = () => {
      dialogVisible.value = false;
      bacthEnvData.targetEnv = '';
    };

    // 批量声明
    const handleBatchStatement = async () => {
      const configIds = ref<string[]>([]);
      if (selectedRow.value.length > 0) {
        configIds.value = selectedRow.value.map(n => n.configId);
      }
      await configRedeclare({
        configIds: configIds.value,
      });
      ElMessage.success('操作成功');
      handleSearch();
    };

    // 声明
    const handleStatement = async (configId:string) => {
      await configRedeclare({
        configIds: [configId],
      });
      ElMessage.success('操作成功！');
      handleSearch();
    };
    const handleBatchEnvSubmit = async () => {
      if (selectedRow.value.length > 0) {
        bacthEnvData.configCodes = selectedRow.value.map(n => n.configCode);
      }
      await formDOM.value?.validate();
      await syncConfigByEnv(bacthEnvData);
      ElMessage.success('同步成功！');
      closeBatchEnv();
      handleSearch();
    };

    return {
      closeBatchEnv,
      formDOM,
      envList,
      bacthEnvData,
      handleBatchEnvSubmit,
      dialogVisible,
      handleSelectionChange,
      showBatchEnv,
      selectedRow,
      params,
      tableTotal,
      tableData,
      tableLoading,
      handleSearch,
      handleReset,
      handleSizeChange,
      handleCurrentChange,
      CONFIG_IS_ENABLED_ENUM,
      CONFIG_IS_ENABLED_LIST,
      handleUpdateConfigEnable,
      editFormConfig,
      handleEditConfigInfo,
      handleEidtSuccess,
      permissionConfig,
      handleImportExcel,
      handleExportExcel,
      handleBatchStatement,
      handleStatement,
    };
  },
});
